import { useEffect, useState } from 'react';
import { getPublicFlags } from '@/lib/api/settings';

// Whether the client portal (v2) is on, decided by the server so an admin can flip it without a deploy.
// Until the answer arrives the page uses the last one this device saw, then the build-time
// VITE_ENABLE_CLIENT_PORTAL, so there is no flash for a returning visitor and a failed request changes nothing.

const STORAGE_KEY = 'casa-aragon:client-portal';
let inFlight: Promise<boolean> | null = null;

const envDefault = (): boolean => import.meta.env.VITE_ENABLE_CLIENT_PORTAL === 'true';

/** Last server answer on this device, else the build-time default. Synchronous. */
export function getClientPortalFlag(): boolean {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'true' || stored === 'false') return stored === 'true';
  } catch {
    // storage unavailable: use the env default
  }
  return envDefault();
}

/** Screens that can switch between the legacy tab (v1) and the native one (v2) from Mantenimiento. */
export type NativeScreen = 'invitados' | 'contrato' | 'pagos';
const NATIVE_SCREENS: NativeScreen[] = ['invitados', 'contrato', 'pagos'];
const nativeKey = (screen: NativeScreen) => `casa-aragon:${screen}-v2`;

/** Last server answer on this device for a native screen; undefined until the server has answered once. Synchronous. */
function getNativeFlag(screen: NativeScreen): boolean | undefined {
  try {
    const stored = localStorage.getItem(nativeKey(screen));
    return stored === 'true' || stored === 'false' ? stored === 'true' : undefined;
  } catch {
    return undefined;
  }
}

// Landing "desde" price: the build-time VITE_ENABLE_LANDING_PRICE until the server (Mantenimiento) has answered once
const LANDING_PRICE_KEY = 'casa-aragon:landing-price';

/** Last server answer on this device for the landing price, else the build-time default. Synchronous. */
export function getLandingPriceFlag(): boolean {
  try {
    const stored = localStorage.getItem(LANDING_PRICE_KEY);
    if (stored === 'true' || stored === 'false') return stored === 'true';
  } catch {
    // storage unavailable: use the env default
  }
  return import.meta.env.VITE_ENABLE_LANDING_PRICE === 'true';
}

/** Asks the server once at a time; never throws (a failure keeps the last known value). */
export function refreshClientPortalFlag(): Promise<boolean> {
  if (inFlight) return inFlight;
  inFlight = getPublicFlags()
    .then((flags) => {
      const enabled = flags.client_portal_enabled === true;
      try {
        localStorage.setItem(STORAGE_KEY, String(enabled));
        for (const screen of NATIVE_SCREENS) {
          localStorage.setItem(nativeKey(screen), String(flags[`${screen}_v2_enabled`] === true));
        }
        localStorage.setItem(LANDING_PRICE_KEY, String(flags.landing_price_enabled === true));
      } catch {
        // ignore
      }
      return enabled;
    })
    .catch(() => getClientPortalFlag())
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

export function useClientPortalEnabled(): boolean {
  const [enabled, setEnabled] = useState(getClientPortalFlag);
  useEffect(() => {
    let active = true;
    void refreshClientPortalFlag().then((value) => {
      if (active) setEnabled(value);
    });
    return () => {
      active = false;
    };
  }, []);
  return enabled;
}

/**
 * A native screen needs both flags: the portal on and the screen's own v2 flag on.
 * `undefined` means the server hasn't answered yet and this device has no stored answer: render a neutral skeleton,
 * not the legacy tab, so a v1 loader never flashes before the v2 screen. A stored answer is used at once.
 */
export function useNativeScreenEnabled(screen: NativeScreen): boolean | undefined {
  const portal = useClientPortalEnabled();
  const [native, setNative] = useState(() => getNativeFlag(screen));
  useEffect(() => {
    let active = true;
    void refreshClientPortalFlag().then(() => {
      if (active) setNative(getNativeFlag(screen));
    });
    return () => {
      active = false;
    };
  }, [screen]);
  if (!portal) return false;
  return native;
}

export function useLandingPriceEnabled(): boolean {
  const [enabled, setEnabled] = useState(getLandingPriceFlag);
  useEffect(() => {
    let active = true;
    void refreshClientPortalFlag().then(() => {
      if (active) setEnabled(getLandingPriceFlag());
    });
    return () => {
      active = false;
    };
  }, []);
  return enabled;
}
